<script setup>
/**
 * The slogan typewriter, rebuilt.
 *
 * The old version animated `width` from 0 with a hand-counted steps(40) per
 * string, so it reflowed every frame and desynced whenever the text changed.
 * This one lays the text out in full immediately and reveals each character
 * with transform and opacity, deriving the timing from the string length.
 *
 * Characters are grouped into words. Each character has to be its own
 * inline-block to animate, and without the word wrapper the browser happily
 * breaks a line in the middle of a word.
 *
 * Under reduced motion the whole thing renders at once, with no caret.
 */
import { computed } from "vue";
import { prefersReducedMotion } from "../../composables/useScrollReveal";

const props = defineProps({
  lines: { type: Array, required: true },
  speed: { type: Number, default: 45 }, // ms per character
  startDelay: { type: Number, default: 300 },
  linePause: { type: Number, default: 500 },
});

const reduced = prefersReducedMotion();

/** Character-level timings, grouped by word, so line 2 follows line 1. */
const rendered = computed(() => {
  let elapsed = props.startDelay;

  return props.lines.map((line) => {
    const words = line.split(" ").map((word, wordIndex) => {
      // Rebuild the space as part of the word, so spacing survives the split.
      const text = wordIndex === 0 ? word : ` ${word}`;
      return Array.from(text).map((char) => {
        const entry = { char, delay: elapsed };
        elapsed += props.speed;
        return entry;
      });
    });

    elapsed += props.linePause;
    return { words, endsAt: elapsed };
  });
});

const totalDuration = computed(() => {
  const last = rendered.value[rendered.value.length - 1];
  return last ? last.endsAt : 0;
});
</script>

<template>
  <div class="typewriter">
    <!-- The accessible copy: one plain, complete string for screen readers. -->
    <span class="visually-hidden">{{ lines.join(". ") }}</span>

    <p
      v-for="(line, lineIndex) in rendered"
      :key="lineIndex"
      class="typewriter__line"
      :class="`typewriter__line--${lineIndex + 1}`"
      aria-hidden="true"
    >
      <template v-if="reduced">{{ lines[lineIndex] }}</template>
      <template v-else>
        <span
          v-for="(word, wordIndex) in line.words"
          :key="wordIndex"
          class="typewriter__word"
        >
          <span
            v-for="(entry, i) in word"
            :key="i"
            class="typewriter__char"
            :style="{ '--char-delay': `${entry.delay}ms` }"
            >{{ entry.char }}</span
          >
        </span>
        <span
          v-if="lineIndex === rendered.length - 1"
          class="typewriter__caret"
          :style="{ '--char-delay': `${totalDuration}ms` }"
        />
      </template>
    </p>
  </div>
</template>

<style scoped>
.typewriter__line {
  margin: 0;
}

/* Keeps a word intact when the line wraps. */
.typewriter__word {
  display: inline-block;
  white-space: nowrap;
}

.typewriter__line--1 {
  font-family: var(--font-display);
  font-size: calc(var(--text-5xl) * var(--display-scale));
  font-weight: var(--weight-bold);
  line-height: var(--display-leading);
  letter-spacing: var(--display-tracking);
  color: var(--ink);
}

/* The promise's second half, in the text face: a subhead, not a headline. */
.typewriter__line--2 {
  margin-block-start: var(--space-4);
  font-size: var(--text-2xl);
  font-weight: var(--weight-medium);
  line-height: var(--leading-snug);
  color: var(--ink-2);
}

/* The caret only appears once the last character has landed. */
.typewriter__caret {
  opacity: 0;
  animation: caret-in 0ms linear forwards, blink 900ms steps(2, start) infinite;
  animation-delay: var(--char-delay, 0ms), var(--char-delay, 0ms);
  margin-inline-start: 2px;
}

@keyframes caret-in {
  to {
    opacity: 1;
  }
}
</style>
